import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Calendar, Users, Settings, FileText, Link as LinkIcon, AlertCircle, CheckCircle, Building2 } from 'lucide-react';
import type { Event } from '../../types';
import { eventApi, handleApiError } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { RoomsConfiguration } from '../../components/RoomsConfiguration';

interface FormErrors {
  [key: string]: string;
}

interface NotificationState {
  type: 'success' | 'error' | null;
  message: string;
}

const EventCreator: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<'basic' | 'details' | 'registration' | 'rooms' | 'settings'>('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [notification, setNotification] = useState<NotificationState>({ type: null, message: '' });
  const [createdEvent, setCreatedEvent] = useState<Event | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'hybrid' as Event['type'],
    startDate: '',
    endDate: '',
    location: '',
    onlineLink: '',
    maxParticipants: 50,
    reviewDuration: 30,
    breakBetweenReviews: 10,
    categories: [] as string[],
    tags: [] as string[],
    expectedOutcomes: [] as string[],
    requirements: [] as string[],
    requiresApproval: true,
    isPublic: true,
    registrationDeadline: '',
  });

  // Check authentication
  if (!isAuthenticated) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <div>
              <h3 className="text-lg font-medium text-red-800">Authentication Required</h3>
              <p className="text-red-700 mt-1">
                You need to be logged in to create events. Please sign in to continue.
              </p>
              <button
                onClick={() => navigate('/signin')}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                Go to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newOutcome, setNewOutcome] = useState('');
  const [newRequirement, setNewRequirement] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const addArrayItem = (field: 'categories' | 'tags' | 'expectedOutcomes' | 'requirements', value: string) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      
      // Clear the input
      if (field === 'categories') setNewCategory('');
      if (field === 'tags') setNewTag('');
      if (field === 'expectedOutcomes') setNewOutcome('');
      if (field === 'requirements') setNewRequirement('');
    }
  };

  const removeArrayItem = (field: 'categories' | 'tags' | 'expectedOutcomes' | 'requirements', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Error display helper component
  const ErrorMessage: React.FC<{ field: string }> = ({ field }) => {
    if (!errors[field]) return null;
    return (
      <p className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1" />
        {errors[field]}
      </p>
    );
  };

  // Success/Error notification component
  const Notification: React.FC = () => {
    if (!notification.type) return null;
    
    return (
      <div className={`fixed top-4 right-4 max-w-md p-4 rounded-lg shadow-lg z-50 ${
        notification.type === 'success' 
          ? 'bg-green-50 border border-green-200' 
          : 'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center">
          {notification.type === 'success' ? (
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
          ) : (
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          )}
          <p className={`text-sm font-medium ${
            notification.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {notification.message}
          </p>
        </div>
      </div>
    );
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Basic info validation
    if (!formData.title.trim()) {
      newErrors.title = 'Event title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Event description is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    // Date validation
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const now = new Date();

      if (startDate < now) {
        newErrors.startDate = 'Start date must be in the future';
      }

      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date';
      }

      // Registration deadline validation
      if (formData.registrationDeadline) {
        const regDeadline = new Date(formData.registrationDeadline);
        if (regDeadline >= startDate) {
          newErrors.registrationDeadline = 'Registration deadline must be before event start';
        }
      }
    }

    // Event type specific validation
    if (formData.type === 'in-person' || formData.type === 'hybrid') {
      if (!formData.location?.trim()) {
        newErrors.location = 'Physical location is required for in-person and hybrid events';
      }
    }

    if (formData.type === 'online' || formData.type === 'hybrid') {
      if (!formData.onlineLink?.trim()) {
        newErrors.onlineLink = 'Online meeting link is required for online and hybrid events';
      } else if (formData.onlineLink && !isValidUrl(formData.onlineLink)) {
        newErrors.onlineLink = 'Please enter a valid URL';
      }
    }

    // Numeric validation
    if (formData.maxParticipants < 1) {
      newErrors.maxParticipants = 'Maximum participants must be at least 1';
    }

    if (formData.reviewDuration < 5) {
      newErrors.reviewDuration = 'Review duration must be at least 5 minutes';
    }

    if (formData.breakBetweenReviews < 0) {
      newErrors.breakBetweenReviews = 'Break time cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string: string): boolean => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const clearNotification = () => {
    setTimeout(() => {
      setNotification({ type: null, message: '' });
    }, 5000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors and notifications
    setErrors({});
    setNotification({ type: null, message: '' });

    // Validate form
    if (!validateForm()) {
      setNotification({
        type: 'error',
        message: 'Please fix the errors above before submitting.'
      });
      clearNotification();
      return;
    }

    setIsLoading(true);

    try {
      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        throw new Error('User not authenticated. Please log in to create events.');
      }

      // Prepare event data for API
      const eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt' | 'currentParticipants'> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        type: formData.type,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        location: formData.location?.trim() || undefined,
        onlineLink: formData.onlineLink?.trim() || undefined,
        maxParticipants: formData.maxParticipants,
        isActive: true,
        reviewDuration: formData.reviewDuration,
        breakBetweenReviews: formData.breakBetweenReviews,
        createdBy: user.id,
        requiresApproval: formData.requiresApproval,
        isPublic: formData.isPublic,
        registrationDeadline: formData.registrationDeadline ? new Date(formData.registrationDeadline) : undefined,
        categories: formData.categories,
        tags: formData.tags,
        expectedOutcomes: formData.expectedOutcomes,
        requirements: formData.requirements,
        // Initialize with default schedule config
        scheduleConfig: {
          id: '',
          eventId: '',
          reviewDuration: formData.reviewDuration,
          breakBetweenReviews: formData.breakBetweenReviews,
          maxConcurrentReviews: 4,
          allowSlotEditing: true,
          dailySchedules: [],
          timezone: 'America/New_York',
          setupTimeMinutes: 15,
          cleanupTimeMinutes: 15,
        },
      };

      // Create the event
      const createdEventResult = await eventApi.create(eventData);
      setCreatedEvent(createdEventResult);

      // Show success notification
      setNotification({
        type: 'success',
        message: `Event "${createdEventResult.title}" created successfully!`
      });

      // Navigate to schedule configuration with the new event ID
      setTimeout(() => {
        navigate(`/admin/events/${createdEventResult.id}/schedule`);
      }, 2000);

    } catch (error) {
      console.error('Error creating event:', error);
      
      const errorMessage = handleApiError(error);
      setNotification({
        type: 'error',
        message: errorMessage
      });
      clearNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Title *
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
            errors.title ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Fall 2025 Portfolio Review Session"
        />
        <ErrorMessage field="title" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description *
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          required
          rows={4}
          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
            errors.description ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Comprehensive portfolio review sessions for design students with industry professionals..."
        />
        <ErrorMessage field="description" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Type *
        </label>
        <select
          name="type"
          value={formData.type}
          onChange={handleInputChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="online">Online Only</option>
          <option value="in-person">In-Person Only</option>
          <option value="hybrid">Hybrid (Online + In-Person)</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date & Time *
          </label>
          <input
            type="datetime-local"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
            required
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
              errors.startDate ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          <ErrorMessage field="startDate" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date & Time *
          </label>
          <input
            type="datetime-local"
            name="endDate"
            value={formData.endDate}
            onChange={handleInputChange}
            required
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
              errors.endDate ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          <ErrorMessage field="endDate" />
        </div>
      </div>

      {(formData.type === 'in-person' || formData.type === 'hybrid') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Physical Location
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
              errors.location ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Design Building, Room 101, University Campus"
          />
          <ErrorMessage field="location" />
        </div>
      )}

      {(formData.type === 'online' || formData.type === 'hybrid') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Online Meeting Link
          </label>
          <input
            type="url"
            name="onlineLink"
            value={formData.onlineLink}
            onChange={handleInputChange}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
              errors.onlineLink ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="https://zoom.us/j/..."
          />
          <ErrorMessage field="onlineLink" />
        </div>
      )}
    </div>
  );

  const renderDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Participants
          </label>
          <input
            type="number"
            name="maxParticipants"
            value={formData.maxParticipants}
            onChange={handleInputChange}
            min="1"
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
              errors.maxParticipants ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          <ErrorMessage field="maxParticipants" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Review Duration (minutes)
          </label>
          <input
            type="number"
            name="reviewDuration"
            value={formData.reviewDuration}
            onChange={handleInputChange}
            min="15"
            step="15"
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
              errors.reviewDuration ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          <ErrorMessage field="reviewDuration" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Break Between Reviews (minutes)
          </label>
          <input
            type="number"
            name="breakBetweenReviews"
            value={formData.breakBetweenReviews}
            onChange={handleInputChange}
            min="0"
            step="5"
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
              errors.breakBetweenReviews ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          <ErrorMessage field="breakBetweenReviews" />
        </div>
      </div>

      {/* Categories */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Categories
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {formData.categories.map((category, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
            >
              {category}
              <button
                type="button"
                onClick={() => removeArrayItem('categories', index)}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="Add category (e.g., UI/UX Design, Web Development)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('categories', newCategory))}
          />
          <button
            type="button"
            onClick={() => addArrayItem('categories', newCategory)}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {formData.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeArrayItem('tags', index)}
                className="ml-2 text-green-600 hover:text-green-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Add tag (e.g., portfolio, resume, career)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('tags', newTag))}
          />
          <button
            type="button"
            onClick={() => addArrayItem('tags', newTag)}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Expected Outcomes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Expected Outcomes
        </label>
        <div className="space-y-2 mb-2">
          {formData.expectedOutcomes.map((outcome, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md"
            >
              <span className="text-sm">{outcome}</span>
              <button
                type="button"
                onClick={() => removeArrayItem('expectedOutcomes', index)}
                className="text-yellow-600 hover:text-yellow-800"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newOutcome}
            onChange={(e) => setNewOutcome(e.target.value)}
            placeholder="Add expected outcome"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('expectedOutcomes', newOutcome))}
          />
          <button
            type="button"
            onClick={() => addArrayItem('expectedOutcomes', newOutcome)}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Requirements */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Requirements
        </label>
        <div className="space-y-2 mb-2">
          {formData.requirements.map((requirement, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-md"
            >
              <span className="text-sm">{requirement}</span>
              <button
                type="button"
                onClick={() => removeArrayItem('requirements', index)}
                className="text-red-600 hover:text-red-800"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newRequirement}
            onChange={(e) => setNewRequirement(e.target.value)}
            placeholder="Add requirement"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('requirements', newRequirement))}
          />
          <button
            type="button"
            onClick={() => addArrayItem('requirements', newRequirement)}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );

  const renderRegistration = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Registration Deadline
        </label>
        <input
          type="datetime-local"
          name="registrationDeadline"
          value={formData.registrationDeadline}
          onChange={handleInputChange}
          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
            errors.registrationDeadline ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        <ErrorMessage field="registrationDeadline" />
        <p className="mt-1 text-sm text-gray-500">
          Leave empty for no deadline
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">Registration Forms</h3>
        <p className="text-blue-800 mb-4">
          After creating this event, you'll be able to set up custom registration forms for both reviewees and reviewers.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-gray-900 mb-2">Reviewee Registration</h4>
            <p className="text-sm text-gray-600 mb-3">
              Students and professionals seeking portfolio/resume reviews
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Personal information</li>
              <li>• Academic/professional background</li>
              <li>• Portfolio/resume upload</li>
              <li>• Specific review goals</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-gray-900 mb-2">Reviewer Registration</h4>
            <p className="text-sm text-gray-600 mb-3">
              Industry professionals volunteering as reviewers
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Professional background</li>
              <li>• Areas of expertise</li>
              <li>• Availability preferences</li>
              <li>• Review capacity</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center">
        <input
          type="checkbox"
          id="requiresApproval"
          name="requiresApproval"
          checked={formData.requiresApproval}
          onChange={handleInputChange}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="requiresApproval" className="ml-2 block text-sm text-gray-900">
          Require approval for registrations
        </label>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isPublic"
          name="isPublic"
          checked={formData.isPublic}
          onChange={handleInputChange}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-900">
          Make event publicly discoverable
        </label>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Event URLs</h3>
        <p className="text-gray-600 mb-4">
          After creating this event, you'll get shareable URLs for registration:
        </p>
        <div className="space-y-3">
          <div className="flex items-center p-3 bg-white border border-gray-200 rounded-md">
            <LinkIcon className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-900">Reviewee Registration</p>
              <p className="text-xs text-gray-500">https://yoursite.com/register/reviewee/[event-id]</p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-white border border-gray-200 rounded-md">
            <LinkIcon className="h-5 w-5 text-gray-400 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-900">Reviewer Registration</p>
              <p className="text-xs text-gray-500">https://yoursite.com/register/reviewer/[event-id]</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRooms = () => (
    <div>
      {createdEvent ? (
        <RoomsConfiguration eventId={createdEvent.id} />
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Create Event First</h3>
            <p className="text-gray-500">
              Please fill out the basic information and create the event before configuring rooms and spaces.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Calendar },
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'registration', label: 'Registration', icon: Users },
    { id: 'rooms', label: 'Rooms & Spaces', icon: Building2 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <>
      <Notification />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/admin/events')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Create New Event</h1>
        </div>
        <p className="text-gray-600">Set up a new portfolio and resume review event</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="mr-2 h-5 w-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          {activeTab === 'basic' && renderBasicInfo()}
          {activeTab === 'details' && renderDetails()}
          {activeTab === 'registration' && renderRegistration()}
          {activeTab === 'rooms' && renderRooms()}
          {activeTab === 'settings' && renderSettings()}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/events')}
            className="px-6 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#2563eb', minHeight: '40px' }} // Force visible styling
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Event
              </>
            )}
          </button>
        </div>
      </form>
    </div>
    </>
  );
};

export default EventCreator;